import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Loader } from '@lms/ui';
import { useLearningSessionContext } from '@/providers/learning-session-context';
import { PDFJS_DOCUMENT_OPTIONS } from '@/lib/pdfjs-document-options';
import { MIN_STAGE_WIDTH, shouldKeepPdfPage, stabilizeMeasuredWidth } from '@/lib/pdf-viewer-layout';
import pdfWorkerSrc from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerSrc;

const MIN_SCALE = 0.6;
const MAX_SCALE = 3;
const SCALE_STEP = 0.15;
const PAGE_BUFFER = 2;
const PAGE_KEEP = 6;
const A4_RATIO = 1.414;

function clampScale(value: number) {
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, Number(value.toFixed(2))));
}

function pinchDistance(a: Touch, b: Touch) {
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
}

export function PdfDocumentViewer({
  file,
  downloadUrl,
  title,
}: {
  file: Blob;
  downloadUrl: string;
  title: string;
}) {
  const session = useLearningSessionContext();
  const viewerRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const pinchRef = useRef<{ distance: number; scale: number } | null>(null);
  const syncRafRef = useRef<number | null>(null);
  const paintedRef = useRef<Set<number>>(new Set());
  const coverCanvasRef = useRef<Map<number, HTMLCanvasElement>>(new Map());
  const [numPages, setNumPages] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1);
  const [baseWidth, setBaseWidth] = useState(0);
  const [paintWidth, setPaintWidth] = useState(0);
  const [covering, setCovering] = useState<Set<number>>(() => new Set());
  const [pageRatio, setPageRatio] = useState(A4_RATIO);
  const [range, setRange] = useState({ start: 1, end: 4 });
  const [keptPages, setKeptPages] = useState<Set<number>>(() => new Set([1, 2, 3, 4]));

  const pdfFile = useMemo(() => {
    if (file.type.startsWith('application/pdf')) return file;
    return new Blob([file], { type: 'application/pdf' });
  }, [file]);

  const sheetWidth = baseWidth > 0 ? Math.floor(baseWidth * scale) : 0;
  const pagePaintWidth = paintWidth > 0 ? paintWidth : sheetWidth;
  const estimatedPageHeight = sheetWidth > 0 ? Math.round(sheetWidth * pageRatio) : 0;

  useEffect(() => {
    return () => {
      if (syncRafRef.current != null) window.cancelAnimationFrame(syncRafRef.current);
    };
  }, []);

  useEffect(() => {
    setNumPages(0);
    setPageNumber(1);
    setScale(1);
    setPageRatio(A4_RATIO);
    setRange({ start: 1, end: 4 });
    setKeptPages(new Set([1, 2, 3, 4]));
    setCovering(new Set());
    paintedRef.current.clear();
  }, [pdfFile]);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;

    const updateWidth = () => {
      const styles = getComputedStyle(el);
      const padding = parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight);
      const next = Math.floor(el.clientWidth - padding);
      setBaseWidth((prev) => stabilizeMeasuredWidth(prev, next));
    };

    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const holdPaintedFrames = useCallback(() => {
    const stage = stageRef.current;
    const next = new Set<number>();
    if (!stage) return next;

    stage.querySelectorAll<HTMLElement>('[data-pdf-page]').forEach((sheet) => {
      const page = Number(sheet.dataset.pdfPage);
      if (!Number.isFinite(page)) return;
      const snap = coverCanvasRef.current.get(page);
      const source = sheet.querySelector<HTMLCanvasElement>('.react-pdf__Page canvas');
      if (!snap) return;

      const canCopy = Boolean(source && paintedRef.current.has(page) && source.width > 0 && source.height > 0);
      if (canCopy && source) {
        snap.width = source.width;
        snap.height = source.height;
        snap.getContext('2d')?.drawImage(source, 0, 0);
        paintedRef.current.delete(page);
        next.add(page);
        return;
      }
      if (snap.width > 0) next.add(page);
    });

    return next;
  }, []);

  useEffect(() => {
    if (sheetWidth <= 0) return;
    if (paintWidth === 0) {
      setPaintWidth(sheetWidth);
      return;
    }
    if (Math.abs(sheetWidth - paintWidth) < 8) return;

    const timer = window.setTimeout(() => {
      setCovering(holdPaintedFrames());
      setPaintWidth(sheetWidth);
    }, 180);

    return () => window.clearTimeout(timer);
  }, [holdPaintedFrames, paintWidth, sheetWidth]);

  const syncVisibleWindow = useCallback(() => {
    const stage = stageRef.current;
    if (!stage || !numPages || sheetWidth < MIN_STAGE_WIDTH) return;

    const sheets = [...stage.querySelectorAll<HTMLElement>('[data-pdf-page]')];
    if (sheets.length === 0) return;

    const y = stage.scrollTop + Math.min(40, stage.clientHeight * 0.08);
    let currentPage = 1;
    let typicalHeight = Math.max(estimatedPageHeight, 1);

    for (const sheet of sheets) {
      const page = Number(sheet.dataset.pdfPage);
      if (!Number.isFinite(page)) continue;
      const top = sheet.offsetTop;
      const height = sheet.offsetHeight || typicalHeight;
      if (height > 1) typicalHeight = height;
      if (y >= top) currentPage = page;
      if (y < top + height) {
        currentPage = page;
        break;
      }
    }

    currentPage = Math.min(numPages, Math.max(1, currentPage));
    setPageNumber((prev) => (prev === currentPage ? prev : currentPage));

    const visibleCount = Math.max(1, Math.ceil(stage.clientHeight / typicalHeight) + 1);
    const start = Math.max(1, currentPage - PAGE_BUFFER);
    const end = Math.min(numPages, currentPage + visibleCount + PAGE_BUFFER);
    setRange((current) => (current.start === start && current.end === end ? current : { start, end }));
    setKeptPages((prev) => {
      const next = new Set<number>();
      for (const page of prev) {
        if (shouldKeepPdfPage(page, start, end, PAGE_KEEP)) next.add(page);
      }
      for (let page = start; page <= end; page += 1) next.add(page);
      if (next.size === prev.size && [...next].every((page) => prev.has(page))) return prev;
      return next;
    });
  }, [estimatedPageHeight, numPages, sheetWidth]);

  const scheduleSyncVisibleWindow = useCallback(() => {
    if (syncRafRef.current != null) return;
    syncRafRef.current = window.requestAnimationFrame(() => {
      syncRafRef.current = null;
      syncVisibleWindow();
    });
  }, [syncVisibleWindow]);

  const goTo = useCallback(
    (next: number) => {
      if (!numPages) return;
      const page = Math.min(numPages, Math.max(1, next));
      setPageNumber(page);
      const stage = stageRef.current;
      const sheet = stage?.querySelector<HTMLElement>(`[data-pdf-page="${page}"]`);
      if (stage && sheet) {
        stage.scrollTo({ top: sheet.offsetTop, behavior: 'smooth' });
      } else if (stage && estimatedPageHeight > 0) {
        stage.scrollTo({ top: (page - 1) * estimatedPageHeight, behavior: 'smooth' });
      }
      session?.markActivity();
    },
    [estimatedPageHeight, numPages, session],
  );

  useEffect(() => {
    syncVisibleWindow();
  }, [syncVisibleWindow, sheetWidth]);

  useEffect(() => {
    if (!session) return;
    const mark = () => session.markActivity();
    mark();

    const root = viewerRef.current;
    const stage = stageRef.current;
    if (!root || !stage) return;

    const onKey = (event: KeyboardEvent) => {
      const inViewer = root.contains(event.target as Node);
      if (!inViewer) return;
      if (event.key === 'ArrowUp' || event.key === 'PageUp') {
        event.preventDefault();
        goTo(pageNumber - 1);
      }
      if (event.key === 'ArrowDown' || event.key === 'PageDown') {
        event.preventDefault();
        goTo(pageNumber + 1);
      }
    };

    const onWheel = (event: WheelEvent) => {
      mark();
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      setScale((current) => clampScale(current + (event.deltaY < 0 ? SCALE_STEP : -SCALE_STEP)));
    };

    const onTouchStart = (event: TouchEvent) => {
      mark();
      if (event.touches.length === 2) {
        pinchRef.current = { distance: pinchDistance(event.touches[0], event.touches[1]), scale };
      }
    };

    const onTouchMove = (event: TouchEvent) => {
      if (event.touches.length !== 2 || !pinchRef.current) return;
      event.preventDefault();
      const distance = pinchDistance(event.touches[0], event.touches[1]);
      const next = clampScale(pinchRef.current.scale * (distance / pinchRef.current.distance));
      setScale(next);
    };

    const onTouchEnd = () => {
      pinchRef.current = null;
    };

    const onScroll = () => {
      mark();
      syncVisibleWindow();
    };

    root.addEventListener('pointerdown', mark);
    stage.addEventListener('scroll', onScroll, { passive: true });
    stage.addEventListener('wheel', onWheel, { passive: false });
    stage.addEventListener('touchstart', onTouchStart, { passive: true });
    stage.addEventListener('touchmove', onTouchMove, { passive: false });
    stage.addEventListener('touchend', onTouchEnd);
    window.addEventListener('keydown', onKey, true);
    return () => {
      root.removeEventListener('pointerdown', mark);
      stage.removeEventListener('scroll', onScroll);
      stage.removeEventListener('wheel', onWheel);
      stage.removeEventListener('touchstart', onTouchStart);
      stage.removeEventListener('touchmove', onTouchMove);
      stage.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('keydown', onKey, true);
    };
  }, [session, numPages, pageNumber, scale, syncVisibleWindow, goTo]);

  const onDocumentLoadSuccess = useCallback(
    async (pdf: { numPages: number; getPage: (pageNumber: number) => Promise<{ getViewport: (opts: { scale: number }) => { width: number; height: number } }> }) => {
      setNumPages(pdf.numPages);
      setPageNumber(1);
      setRange({ start: 1, end: Math.min(pdf.numPages, 4) });
      try {
        const firstPage = await pdf.getPage(1);
        const viewport = firstPage.getViewport({ scale: 1 });
        if (viewport.width > 0) {
          setPageRatio(viewport.height / viewport.width);
        }
      } catch {
        setPageRatio(A4_RATIO);
      }
      session?.markActivity();
    },
    [session],
  );

  const pages = useMemo(() => Array.from({ length: numPages }, (_, index) => index + 1), [numPages]);

  return (
    <div
      ref={viewerRef}
      className="lms-pdf-viewer"
      aria-label={numPages > 0 ? `${title}. Página ${pageNumber} de ${numPages}` : title}
    >
      <div ref={stageRef} className="lms-pdf-viewer__stage" tabIndex={0}>
        <Document
          file={pdfFile}
          key={downloadUrl}
          options={PDFJS_DOCUMENT_OPTIONS}
          suspense={false}
          loading={
            <div className="lms-pdf-viewer__status">
              <Loader label="Cargando PDF…" />
            </div>
          }
          error={<p className="lms-pdf-viewer__error">No se pudo mostrar el PDF. Prueba descargarlo.</p>}
          noData={<p className="lms-pdf-viewer__error">No hay un PDF para mostrar.</p>}
          onLoadSuccess={onDocumentLoadSuccess}
          onItemClick={({ pageNumber: next }) => {
            if (next) goTo(next);
          }}
        >
          {baseWidth > 0 && numPages > 0
            ? pages.map((page) => {
                const active = keptPages.has(page);
                return (
                  <div
                    key={page}
                    data-pdf-page={page}
                    className="lms-pdf-viewer__sheet"
                    style={{
                      width: sheetWidth,
                      minHeight: estimatedPageHeight || undefined,
                    }}
                  >
                    <div className="lms-pdf-viewer__pager">
                      <span className="lms-pdf-viewer__page-label">
                        {page} / {numPages}
                      </span>
                    </div>
                    {active ? (
                      <>
                        <Page
                          pageNumber={page}
                          width={pagePaintWidth}
                          scale={1}
                          suspense={false}
                          loading={null}
                          renderAnnotationLayer={false}
                          renderTextLayer={false}
                          onRenderSuccess={() => {
                            paintedRef.current.add(page);
                            setCovering((current) => {
                              if (!current.has(page)) return current;
                              const next = new Set(current);
                              next.delete(page);
                              return next;
                            });
                            scheduleSyncVisibleWindow();
                          }}
                        />
                        <canvas
                          ref={(node) => {
                            if (node) coverCanvasRef.current.set(page, node);
                            else coverCanvasRef.current.delete(page);
                          }}
                          className={`lms-pdf-viewer__snapshot${covering.has(page) ? ' is-on' : ''}`}
                          aria-hidden
                        />
                      </>
                    ) : (
                      <div
                        className="lms-pdf-viewer__sheet-placeholder"
                        style={{ height: Math.round(sheetWidth * pageRatio) }}
                      />
                    )}
                  </div>
                );
              })
            : null}
        </Document>
      </div>
    </div>
  );
}
